import { cn } from '@poseidon/components/cn';
import { Button, Tooltip } from '@poseidon/components/Input2';
import { General } from '@poseidon/components/StrokeIcons';

import { STEP_META, stepIssueRingClass, stepLabel, type SequenceStep, type StepIssue } from '~/components/Sequence/sequenceSteps';
import { SequenceAwaitNode } from '~/components/Sequence/SequenceAwaitNode';
import { SequenceWaitNode } from '~/components/Sequence/SequenceWaitNode';
import { type WaitUnit } from '~/components/Sequence/WaitDurationInput';

interface Props {
  step: SequenceStep;
  dragging: boolean;
  /** A validation problem with this step (e.g. "Await accept" before any Connect), or null. Errors ring red,
   *  advisory warnings ring amber. */
  invalid: StepIssue | null;
  /** Remove this step. Omit (or `readOnly`) → no remove ✕. */
  onRemove?: () => void;
  /** Commit a new wait length (canonical days) + display unit for a `wait` step. Omit (or `readOnly`) → static. */
  onChangeWait?: (days: number, unit: WaitUnit) => void;
  /** Commit a new acceptance deadline for an `await_connect` step. Omit (or `readOnly`) → static. */
  onChangeDeadlineDays?: (days: number) => void;
  /**
   * Locked view (the per-contact contact-detail graph): freeze structure — no remove ✕, and the
   * `wait` / `await_connect` durations render as STATIC pills (a per-contact override can't change delays).
   */
  readOnly?: boolean;
  /**
   * Locked view only: when this contact is currently parked on this step's call/reminder to-do, mark it done
   * and advance the sequence past the manual step. Rendered as a green "Mark done" control in the left gutter
   * (the people-pile spot, free in the per-contact graph). Omit → no control (not a parked manual task).
   */
  onMarkDone?: () => void;
}

/**
 * A single step in the sequence graph — an action step is a small rounded icon tile; a wait step is
 * a rounded-full pill with its editable duration. Clicking the node opens its content editor; dragging
 * it (past a small threshold — see {@link SequenceGraphEditor}) reorders it. Delete slides in on hover.
 * An invalid step gets a red ring + a reason tooltip.
 */
export function SequenceGraphNode({
  step,
  dragging,
  invalid,
  onRemove,
  onChangeWait,
  onChangeDeadlineDays,
  readOnly = false,
  onMarkDone,
}: Props) {
  const chipBase = cn(
    !readOnly && 'cursor-grab',
    'shadow-sm transition-shadow hover:shadow-md',
    dragging && 'cursor-grabbing shadow-lg ring-2 ring-blue-400',
    !dragging && stepIssueRingClass(invalid),
  );
  // wait / await_connect render as editable "N days" pills; every other step is a plain icon tile.
  const isDurationStep = step.type === 'wait' || step.type === 'await_connect';

  return (
    <div className="group/node relative inline-flex items-center">
      {!isDurationStep && (
        <Tooltip
          as="span"
          tooltip={invalid?.message ?? stepLabel(step)}
          className={cn(
            'flex size-9 items-center justify-center rounded-lg border border-lynch-200 bg-white text-lynch-600 dark:border-slate-700 dark:bg-slate-900 dark:text-lynch-300 [&>svg]:size-5',
            chipBase,
          )}>
          {STEP_META[step.type].icon}
        </Tooltip>
      )}
      {/* A wait renders as a compact static pill; the editable duration opens in a popover on click. */}
      {step.type === 'wait' && (
        <SequenceWaitNode step={step} chipBase={chipBase} onChangeWait={readOnly ? undefined : onChangeWait} />
      )}
      {step.type === 'await_connect' && (
        <SequenceAwaitNode
          step={step}
          chipBase={chipBase}
          invalid={invalid}
          onChangeDeadlineDays={readOnly ? undefined : onChangeDeadlineDays}
        />
      )}

      {/* Left-gutter "Mark done" (locked view only) — where the people-pile sits on the team graph, free in
          the per-contact graph. Shown only when this contact is parked on this step's call/reminder to-do;
          clicking clears the to-do and advances the sequence past the manual step. */}
      {onMarkDone != null && (
        <Button
          size="2xs"
          color="green"
          tone="soft"
          icon={<General.CheckCircle />}
          tooltip="Mark done — complete this to-do and advance the sequence"
          onClick={onMarkDone}
          className="absolute right-full top-1/2 mr-2 -translate-y-1/2 whitespace-nowrap">
          Mark done
        </Button>
      )}

      {/* Corner ✕ (matches the parallel-chip delete), confirm-first so a stray click can't drop a step.
          Hidden in the locked (per-contact) view — structure can't change there. */}
      {!readOnly && onRemove != null && (
        <Button
          size="2xs"
          color="red"
          tone="soft"
          aria-label="Remove step"
          tooltip="Remove step"
          icon={<General.XMark />}
          onClick={onRemove}
          confirm={{ title: 'Remove this step?', confirmLabel: 'Remove', position: 'top span-left' }}
          className={cn(
            'absolute -right-1.5 -top-1.5',
            'pointer-events-none opacity-0 transition-opacity group-hover/node:pointer-events-auto group-hover/node:opacity-100',
          )}
        />
      )}
    </div>
  );
}
